import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Lock, Download, LogOut, Loader2, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import { VideoRequestsTable } from "@/components/VideoRequestsTable";
import { BookOrdersAdmin } from "@/components/books/BookOrdersAdmin";
import { BookReviewsAdmin } from "@/components/books/BookReviewsAdmin";

const TOKEN_KEY = "mm_admin_token";

const COLS = [
  { key: "created_at", label: "Date" },
  { key: "email", label: "Email" },
  { key: "collection", label: "Collection" },
  { key: "color", label: "Color" },
  { key: "size", label: "Size" },
  { key: "child_name", label: "Child" },
];

export default function AdminPreOrders() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const fmtDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pre-orders", { headers: { Authorization: `Bearer ${t}` } });
      setRows(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired. Please enter the passcode again.");
      } else {
        toast.error("Could not load pre-orders.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { passcode });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPasscode("");
    } catch {
      toast.error("Incorrect passcode.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRows(null);
  };

  const downloadCsv = () => {
    if (!rows?.length) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = COLS.map((c) => c.label).join(",");
    const lines = rows.map((r) =>
      COLS.map((c) => esc(c.key === "created_at" ? fmtDate(r[c.key]) : r[c.key])).join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mimi-me-preorders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Passcode gate ----
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-gate">
        <div className="noise" />
        <form onSubmit={login} className="w-full max-w-md relative">
          <div className="p-9 rounded-3xl bg-white border shadow-[0_30px_80px_rgba(11,27,61,0.15)]" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: "var(--navy)" }}>
              <Lock size={20} style={{ color: "var(--gold)" }} />
            </div>
            <h1 className="font-display text-3xl" style={{ color: "var(--navy)" }}>Owner access</h1>
            <p className="font-body font-light text-sm mt-2 mb-6" style={{ color: "var(--muted)" }}>
              Enter your passcode to view pre-orders and video requests.
            </p>
            <input
              type="password" required value={passcode} onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              className="w-full rounded-full bg-white px-6 py-4 font-body text-sm outline-none border mb-4"
              style={{ borderColor: "rgba(11,27,61,0.2)", color: "var(--navy)" }}
              data-testid="admin-passcode-input"
            />
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="admin-login-btn">
              {loading ? <Loader2 size={18} className="animate-spin" /> : "Enter"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="admin-preorders-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="overline mb-3" style={{ color: "var(--gold)" }}>Owner dashboard</p>
            <h1 className="font-display text-4xl md:text-5xl" style={{ color: "var(--navy)" }}>Rash guard pre-orders</h1>
            <p className="font-body font-light mt-2" style={{ color: "var(--muted)" }} data-testid="admin-count">
              {rows ? `${rows.length} ${rows.length === 1 ? "signup" : "signups"}` : "Loading…"}
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={downloadCsv} disabled={!rows?.length} className="btn-primary" data-testid="admin-download-csv">
              <Download size={16} /> Download CSV
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border font-body text-sm" style={{ borderColor: "rgba(11,27,61,0.2)", color: "var(--navy)" }} data-testid="admin-logout">
              <LogOut size={16} /> Log out
            </button>
          </div>
        </div>

        {loading && !rows ? (
          <div className="flex items-center gap-2 py-20 justify-center" style={{ color: "var(--muted)" }}>
            <Loader2 className="animate-spin" size={20} /> Loading pre-orders…
          </div>
        ) : rows && rows.length === 0 ? (
          <div className="text-center py-20" data-testid="admin-empty">
            <ShoppingBag size={32} className="mx-auto mb-4" style={{ color: "var(--gold)" }} />
            <p className="font-display text-2xl" style={{ color: "var(--navy)" }}>No pre-orders yet</p>
            <p className="font-body font-light mt-2" style={{ color: "var(--muted)" }}>Signups from the Merch page will appear here.</p>
          </div>
        ) : (
          <div className="rounded-2xl border overflow-x-auto bg-white" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
            <table className="w-full text-left border-collapse" data-testid="admin-table">
              <thead>
                <tr style={{ backgroundColor: "var(--navy)" }}>
                  {COLS.map((c) => (
                    <th key={c.key} className="px-5 py-4 font-body text-xs uppercase tracking-wider text-white/90 whitespace-nowrap">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows?.map((r, i) => (
                  <tr key={r.email + i} className="border-t" style={{ borderColor: "rgba(11,27,61,0.08)" }} data-testid="admin-row">
                    <td className="px-5 py-4 font-body text-sm whitespace-nowrap" style={{ color: "var(--muted)" }}>{fmtDate(r.created_at)}</td>
                    <td className="px-5 py-4 font-body text-sm" style={{ color: "var(--navy)" }}>{r.email}</td>
                    <td className="px-5 py-4 font-body text-sm" style={{ color: "var(--navy)" }}>{r.collection || "-"}</td>
                    <td className="px-5 py-4 font-body text-sm" style={{ color: "var(--navy)" }}>{r.color || "-"}</td>
                    <td className="px-5 py-4 font-body text-sm" style={{ color: "var(--navy)" }}>{r.size || "-"}</td>
                    <td className="px-5 py-4 font-body text-sm" style={{ color: "var(--navy)" }}>{r.child_name || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <VideoRequestsTable token={token} fmtDate={fmtDate} />
        <BookOrdersAdmin token={token} fmtDate={fmtDate} />
        <BookReviewsAdmin token={token} />
      </div>
    </div>
  );
}
